import BoardEmptyState from '@/components/BoardEmptyState';
import ConfirmDialog from '@/components/ConfirmDialog';
import Constants from '@/Constants';
import { Question } from '@/models/QuestionCreateData';
import { ApplicationState } from '@/store';
import { deleteQuestionRequest, putQuestionReorderRequest } from '@/store/evaluationQuestion/actions';
import { getLessonRequest } from '@/store/lesson/action';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, DraggableProvided, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import QuestionCard from '../QuestionCard';
import './style.scss';

interface Props {
    onAddQuestionClick: () => void;
    setShowQuestionModal: (value: boolean) => void;
}

const QuestionList: React.FC<Props> = (props: Props) => {
    const { strings, currentLessonInEdition, questions, evaluation, successOnDelete, currentCourseInEdition } = useSelector(
        (state: ApplicationState) => ({
            strings: state.locale.data,
            currentLessonInEdition: state.lessons.currentData,
            evaluation: state.lessons.evaluation,
            questions: state.evaluationQuestion.list.questions,
            successOnDelete: state.evaluationQuestion.successOnDelete,
            currentCourseInEdition: state.lists.courseData,
        }),
    );

    const [questionList, setQuestionList] = useState<Question[]>([]);
    const [questionToDelete, setQuestionToDelete] = useState<Question>();
    const dispatch = useDispatch();

    const onDragEnd = (result: DropResult): void => {
        const { source, destination } = result;
        if (evaluation && destination && source.droppableId === destination.droppableId) {
            const result = [...questionList];
            const [movedQuestion]: Question[] = result.splice(source.index, 1);
            const questionOrder = result[destination.index] ? result[destination.index].questionOrder : result.length;

            dispatch(putQuestionReorderRequest(evaluation.topics[0].id || 0, (movedQuestion.id && movedQuestion.id) || 0, questionOrder));

            result.splice(destination.index, 0, movedQuestion);
            setQuestionList(
                result.map((e, index) => {
                    e.questionOrder = index;
                    return e;
                }),
            );
        }
    };

    useEffect(() => {
        if (questions) {
            setQuestionList(questions);
        }
    }, [questions]);

    const onDeleteClickHandler = (question: Question) => {
        setQuestionToDelete(question);
    };

    useEffect(() => {
        if (currentLessonInEdition && successOnDelete && questionToDelete && questionToDelete.id) {
            setQuestionToDelete(undefined);
            dispatch(getLessonRequest(currentLessonInEdition.id, false, true));
        }
    }, [successOnDelete]);

    const { onAddQuestionClick, setShowQuestionModal } = props;

    return (
        <>
            <ConfirmDialog
                content={strings.DELETE_QUESTION_MESSAGE}
                leftClick={() => {
                    currentLessonInEdition &&
                        questionToDelete &&
                        questionToDelete.id &&
                        dispatch(deleteQuestionRequest(questionToDelete.id, currentLessonInEdition.id));
                }}
                rightClick={() => setQuestionToDelete(undefined)}
                show={!!questionToDelete}
            />
            {questionList.length === 0 ? (
                <BoardEmptyState className="empty-state-question-board" icon={Constants.icons.ASSIGNMENT} title={strings.EMPTY_QUESTION_BOARD_TITLE}>
                    <div className="class-card-action" style={{ display: 'block' }}>
                        <button className="btn btn-link btn--info" onClick={onAddQuestionClick} type="button">
                            <span>{strings.ADD_QUESTIONS}</span>
                            <i className="material-icons-outlined align-middle arrow-right">{Constants.icons.ARROW_RIGHT}</i>
                        </button>
                    </div>
                </BoardEmptyState>
            ) : (
                <>
                    <div className="question-list-main-container">
                        <div className="title-container">
                            <span>{strings.QUESTIONS}</span>
                        </div>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="droppable">
                                {(provided: DroppableProvided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps} className="draggable-list question-list-container">
                                        {questionList &&
                                            questionList
                                                .sort(function(q1, q2) {
                                                    return q1.questionOrder - q2.questionOrder;
                                                })
                                                .map((question, index) => (
                                                    <Draggable
                                                        draggableId={(question.id && question.id.toString()) || ''}
                                                        index={index}
                                                        key={question.id}
                                                    >
                                                        {(providedDraggable: DraggableProvided) => (
                                                            <div
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.draggableProps}
                                                                {...providedDraggable.dragHandleProps}
                                                                className="draggable-list-item"
                                                            >
                                                                <i className="material-icons-outlined align-middle drag-indicator-icon">
                                                                    drag_indicator
                                                                </i>
                                                                <span className="draggable-list-item-card">
                                                                    <QuestionCard
                                                                        onDelete={onDeleteClickHandler}
                                                                        question={question}
                                                                        setShowQuestionModal={setShowQuestionModal}
                                                                    />
                                                                </span>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                        {currentCourseInEdition.status !== Constants.courseStatus.PUBLISHED && (
                            <div className="add-question">
                                <i className="material-icons" onClick={onAddQuestionClick}>
                                    {Constants.icons.ADD}
                                </i>
                                <span onClick={onAddQuestionClick}>{strings.ADD_QUESTIONS}</span>
                            </div>
                        )}
                    </div>
                    <hr />
                </>
            )}
        </>
    );
};

export default QuestionList;
